import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ProcessedErrorResponse,
  processError,
  processErrorResponse,
} from '@sdir/utilities/lib/helpers/errorHandling';
import {
  BaseTracker,
  SubmitItemResult,
  SubmitItem,
  SubmitStatus,
} from '../../types/submitSequence';
import { buildSubmitPromiseChain, SubmitChainResult } from '../../helpers/submitSequenceHelpers';

export const useSubmitSequence = <TParams, TTracker extends BaseTracker = BaseTracker>(
  sequenceFactory: (p: TParams) => SubmitItem<TTracker>[]
): {
  submit: (
    p: TParams,
    tracker: TTracker
  ) => Promise<{ tracker: TTracker; results: SubmitItemResult<TTracker>[] }>;
  isSubmitting: boolean;
  results?: SubmitItemResult<TTracker>[];
} => {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<SubmitItemResult<TTracker>[]>();

  const processSubmitQueue = useCallback(
    async (p: TParams, tracker: TTracker) => {
      const initialContext: TTracker = {
        ...tracker,
        errors: null,
      };

      const mapProcessedError = (processed: ProcessedErrorResponse): SubmitStatus => {
        if (processed.realError) {
          return {
            success: false,
            errors: processed?.messages || [
              intl.formatMessage({ id: 'submitsteps.requestfailed' }),
            ],
          };
        }
        return {
          success: true,
          result: processed?.originalResponse?.data,
        };
      };

      const performQueuedApiCall = async (
        item: SubmitItem<TTracker>,
        ctx: TTracker
      ): Promise<SubmitStatus> => {
        try {
          setIsSubmitting(true);
          const response = await item.apiCall(ctx);
          if (response.status >= 400 && response.status < 600) {
            const processed = processErrorResponse(response, intl);
            return mapProcessedError(processed);
          }
          return { success: true, result: response.data };
        } catch (e: any) {
          const processed = processError(e, intl);
          return mapProcessedError(processed);
        } finally {
          setIsSubmitting(false);
        }
      };

      try {
        const submitSequence = sequenceFactory(p);
        return buildSubmitPromiseChain<TTracker>(
          initialContext,
          submitSequence,
          performQueuedApiCall
        );
      } catch (e: any) {
        const processed = processError(e, intl);
        const errors = processed.messages || [
          e?.message || intl.formatMessage({ id: 'general.error.unknownerror' }),
        ];
        return Promise.resolve<SubmitChainResult<TTracker>>({
          tracker: { ...initialContext, errors },
          results: [
            {
              status: { errors, success: false, result: null },
              item: {
                labelId: 'general.error.buildsubmitsequence',
                apiCall: () => Promise.reject(),
              },
            },
          ],
        });
      }
    },
    [intl, sequenceFactory]
  );

  const submit = useCallback(
    (p: TParams, tracker: TTracker) => {
      setResults([]);
      return processSubmitQueue(p, tracker).then((r) => {
        setResults(r.results);
        return r;
      });
    },
    [processSubmitQueue]
  );

  return { submit, isSubmitting, results };
};
